import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle, ChevronDown } from 'lucide-react';

export const Hero = () => {
  const { t } = useLanguage();

  const scrollToMenu = () => {
    const el = document.querySelector('#menu');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToLocations = () => {
    const el = document.querySelector('#locations');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToNext = () => {
    const el = document.querySelector('#about');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* YouTube Video Background - Desktop Only */}
      <div className="absolute inset-0 hidden md:block">
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <iframe
            className="absolute top-1/2 left-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            src="https://www.youtube.com/embed/QKgdese-oJo?autoplay=1&mute=1&loop=1&playlist=QKgdese-oJo&controls=0&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&disablekb=1&playsinline=1"
            title="Background Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              pointerEvents: 'none',
            }}
          />
        </div>
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-[#0C4A6E]/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C4A6E]/80 via-[#0C4A6E]/50 to-transparent" />
      </div>

      {/* Mobile Background Image - Shown only on mobile */}
      <div className="absolute inset-0 md:hidden">
        <img
          src="/assets/images/hero/hero-mobile.png"
          alt="Best mariscos restaurant in Los Cabos - Cristian's Baja Salada authentic Sinaloa seafood"
          className="w-full h-full object-cover object-center"
          fetchpriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C4A6E]/90 via-[#0C4A6E]/65 to-[#0C4A6E]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C4A6E]/60 via-transparent to-[#0C4A6E]/40" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-[#0D9488]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-[#2DD4BF]/8 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full py-32">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8">
              <div className="w-2 h-2 rounded-full bg-[#2DD4BF] animate-pulse" />
              <span className="text-sm font-medium text-white/90 tracking-wide">Los Cabos, B.C.S.</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6"
          >
            {t('hero.headline')}
          </motion.h1>
          {/* Hidden SEO H1 for search engines */}
          <h1 className="sr-only">Cristian's Baja Salada – Best Mariscos in Los Cabos</h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-lg sm:text-xl text-white/80 leading-relaxed mb-10 max-w-xl"
          >
            {t('hero.subheadline')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={scrollToMenu}
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0D9488] text-white font-semibold rounded-xl hover:bg-[#0F766E] transition-colors duration-300 shadow-lg shadow-[#0D9488]/25"
            >
              {t('hero.viewMenu')}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              onClick={scrollToLocations}
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/25 hover:bg-white/20 transition-colors duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              {t('hero.reserve')}
            </button>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.button
        onClick={scrollToNext}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown className="w-6 h-6" />
        </motion.div>
      </motion.button>
    </section>
  );
};
